import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { TWebsiteBuilderNode, isWebsiteBuilderElementParentNode } from "../nodes/websiteBuilderNode";
import { WebsiteBuilderRootView } from "./views/components/root/WebsiteBuilderRootView";
import "./websiteBuilderDesignSpace.scss";
import "./styles/editor.scss";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { websiteBuilderSelectNode } from "redux/actions/websiteBuilder/websiteBuilder";
import { WebsiteBuilderSectionView } from "./views/components/section/WebsiteBuilderSectionView";
import { websiteBuilderZLayers } from "../websiteBuilderZLayers";
import { useWebsiteBuilderContext } from "../websiteBuilderContext";
import { IWebsiteBuilderDesignSpaceBoundaries } from "../WebsiteBuilderEditor";
import { WebsiteBuilderElementView } from "./views/components/element/WebsiteBuilderElementView";

export const WEBSITE_BUILDER_DESIGN_PAGE_WIDTH = 1000;

interface IWebsiteBuilderDesignSpaceProps {
  setDesignSpaceBoundaries: React.Dispatch<React.SetStateAction<IWebsiteBuilderDesignSpaceBoundaries>>;
}

interface IDesignSpaceSectionResizeState {
  scrollBarExists: boolean;
  bottomSpacerHeight: number;
}

export function WebsiteBuilderDesignSpace(props: IWebsiteBuilderDesignSpaceProps) {
  const dispatch = useAppDispatch();

  const { setDesignSpaceBoundaries } = props;

  const { designSpaceBoundaries, scrollContainerRef } = useWebsiteBuilderContext();

  const designSpaceRef = useRef<HTMLDivElement>(null);

  const [sectionResizeState, setSectionResizeState] = useState<IDesignSpaceSectionResizeState>({
    scrollBarExists: false,
    bottomSpacerHeight: 0,
  });

  const selectedNode = useAppSelector(store => store.websiteBuilderStore.selectedNode);
  const editingMode = selectedNode?.editingMode;

  const tree = useAppSelector(store => store.websiteBuilderStore.tree);

  const handlePointerDownOutsideDesignSpace = useCallback((event: PointerEvent) => {
    if (designSpaceRef.current && event.target instanceof Node && !designSpaceRef.current.contains(event.target)) {
      dispatch(
        websiteBuilderSelectNode({
          selectedNode: null,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (selectedNode) {
      document.addEventListener("pointerdown", handlePointerDownOutsideDesignSpace);
    }

    return () => {
      document.removeEventListener("pointerdown", handlePointerDownOutsideDesignSpace);
    };
  }, [selectedNode, handlePointerDownOutsideDesignSpace]);

  useLayoutEffect(() => {
    let observer: ResizeObserver = null;
    let sectionResizing = false;

    const scrollContainer = scrollContainerRef.current;
    const designSpace = designSpaceRef.current;

    if (!designSpace) {
      return;
    }

    sectionResizing = editingMode === "section-resize";

    const update = () => {
      if (sectionResizing && scrollContainer) {
        requestAnimationFrame(() => {
          const scrollBarExists = scrollContainer.scrollHeight > scrollContainer.clientHeight;
          const bottomSpacerHeight = scrollBarExists ? scrollContainer.clientHeight : 0;

          setSectionResizeState(prev => ({
            ...prev,
            scrollBarExists,
            bottomSpacerHeight,
          }));
        });
      }

      requestAnimationFrame(() => {
        const designSpaceWidth = designSpace?.getBoundingClientRect()?.width;

        if (designSpaceWidth == null || designSpaceWidth === 0) {
          return;
        }

        const boundaryGap = (designSpaceWidth - WEBSITE_BUILDER_DESIGN_PAGE_WIDTH) / 2;

        const updatedLeftX = boundaryGap;
        const updatedRightX = designSpaceWidth - boundaryGap;

        setDesignSpaceBoundaries(prev => {
          if (Math.abs(prev.leftX - updatedLeftX) < 1 && Math.abs(prev.rightX - updatedRightX) < 1) {
            return prev;
          } else {
            return {
              leftX: updatedLeftX,
              rightX: updatedRightX,
            };
          }
        });
      });
    };

    update();

    observer = new ResizeObserver(update);
    observer.observe(designSpace);

    return () => {
      observer?.disconnect();

      if (sectionResizing) {
        setSectionResizeState(prev => ({
          ...prev,
          scrollBarExists: false,
          bottomSpacerHeight: 0,
        }));
      }
    };
  }, [editingMode]);

  function renderView(node: TWebsiteBuilderNode, parentNode: TWebsiteBuilderNode) {
    if (node == null) {
      return null;
    } else if (node.type === "root") {
      return (
        <WebsiteBuilderRootView
          key={node.id}
          node={node}
          header={renderView(node.header, node)}
          footer={renderView(node.footer, node)}
        >
          {node.children?.map(child => renderView(child, node))}
        </WebsiteBuilderRootView>
      );
    } else if (node.type === "section" && parentNode.type === "root") {
      return (
        <WebsiteBuilderSectionView key={node.id} parentNode={parentNode} node={node}>
          {node.children?.map(child => renderView(child, node))}
        </WebsiteBuilderSectionView>
      );
    } else if (node.type === "container" && parentNode.type === "section") {
      return null;
    } else if (node.type === "element" && isWebsiteBuilderElementParentNode(parentNode)) {
      return <WebsiteBuilderElementView key={node.id} parentNode={parentNode} node={node} />;
    } else {
      return null;
    }
  }

  return (
    <div
      className="website-builder-design-space-outer-frame"
      style={{
        zIndex: websiteBuilderZLayers.designSpace.outerFrame,
      }}
    >
      <div ref={scrollContainerRef} className="design-space-scroll-container">
        <div ref={designSpaceRef} className="design-space">
          {renderView(tree, null)}
          <div
            className="design-space-boundary"
            style={{
              left: designSpaceBoundaries.leftX,
              zIndex: websiteBuilderZLayers.designSpace.sectionEditorUI,
            }}
          />
          <div
            className="design-space-boundary"
            style={{
              left: designSpaceBoundaries.rightX,
              zIndex: websiteBuilderZLayers.designSpace.sectionEditorUI,
            }}
          />
          {editingMode === "section-resize" && sectionResizeState.scrollBarExists && (
            <div style={{ height: sectionResizeState.bottomSpacerHeight }} />
          )}
        </div>
      </div>
    </div>
  );
}
